import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import Button from 'components/Button';
import CrossIcon from 'svg/cross.svg';

const FileUpload = ({
  id,
  name,
  allowedFileTypes,
  onChange,
  setRef,
  test,
  extraClasses,
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const [fileInput, setFileInput] = useState(useRef());
  const MAX_MB = 64;
  // This is in bytes
  const MAX_FILE_SIZE = MAX_MB * 1024 * 1024;
  // Make an array of all accepted mimetypes
  const allowedMimetypes = allowedFileTypes.map(fileType => fileType.mimeType);
  // Make an array of all the accepted filetypes to show
  const allowedFileTypesLabels = allowedFileTypes
    .map(fileType => fileType.label)
    .filter(label => label !== undefined);
  // This function validated the given file input
  const validateFileInput = e => {
    // Clear any previous errors
    setErrors([]);
    const {
      target: {
        validity,
        files: [file],
      },
    } = e;
    // If no file provided or HTML validation fails just stop here
    if (!file || !validity.valid) return;
    const { size, type } = file;

    // If the file size doesn't exceed max filesize and file type is allowed,
    // pass the event with file to the passed onchange function
    if (size <= MAX_FILE_SIZE && allowedMimetypes.includes(type))
      return onChange(e);

    // Keep track of all validation errors
    let currentErrors = [];
    if (size > MAX_FILE_SIZE) {
      currentErrors = [...currentErrors, t('fileUpload.fileTooLargeError')];
    }
    if (!allowedMimetypes.includes(type)) {
      currentErrors = [
        ...currentErrors,
        t('fileUpload.fileTypeNotAllowedError'),
      ];
    }
    // Update the state with the errors
    setErrors(currentErrors);
  };

  const emptyFileInput = () => {
    fileInput.value = '';
    onChange({ target: fileInput, value: null });
    validateFileInput({ target: fileInput });
  };

  return (
    <div className={`c-form__row ${extraClasses || ''}`.trim()}>
      <div className="c-form__input-container">
        <label htmlFor={id} hidden>
          <div className="c-form__label u-visually-hidden">
            <span className="c-form__label-inner">{t('fileUpload.label')}</span>
          </div>
          <div className="c-form__file-upload-container">
            <input
              type="file"
              data-cy={test}
              id={id}
              name={name}
              accept={allowedMimetypes.join()}
              onChange={e => validateFileInput(e)}
              className={`c-input-text ${
                errors?.length ? 'is-invalid' : ''
              }`.trim()}
              ref={el => {
                setFileInput(el);
                if (setRef) setRef(el);
              }}
            />
            {!!fileInput?.value && (
              <Button
                label={t('fileUpload.removeFile')}
                Icon={CrossIcon}
                hideLabel
                extraClasses="c-form__empty-file-upload c-button--link c-button--chevron c-button--red"
                onClick={emptyFileInput}
              />
            )}
          </div>
        </label>
        {!!errors?.length && (
          <div className="c-form__error">
            {errors
              .map(error => <span key={error}>{error}</span>)
              .reduce((prev, curr) => [prev, ' ', curr])}
          </div>
        )}
        {allowedFileTypes && (
          <div className="c-form__hint c-form__hint--under-input">
            {t('fileUpload.allowedTypes')}: <br />
            {allowedFileTypesLabels
              .map(label => <span key={label}>{label}</span>)
              .reduce((prev, curr, currentIndex) => [
                prev,
                `${
                  currentIndex + 1 === allowedFileTypesLabels.length
                    ? ` ${t('and.label')} `
                    : ', '
                }`,
                curr,
              ])}
          </div>
        )}
      </div>
    </div>
  );
};

FileUpload.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  allowedFileTypes: PropTypes.array,
  onChange: PropTypes.func,
  extraClasses: PropTypes.string,
};

export default FileUpload;
